import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, PageContainer, RadioInput, Typography } from '@guildeducationinc/recess';
import { FormActions } from '@src/components/Employee/FormActions/FormActions';
import { ProgramDetails } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/ProgramDetails';
import { FlowContainer } from '@src/components/FlowContainer/FlowContainer';
import { FullPageWrapper } from '@src/components/FullPageWrapper';
import { TopBar } from '@src/components/TopBar';
import { useMyBenefits } from '@src/hooks/useMyBenefits';
import { usePaymentRequests } from '@src/hooks/usePaymentRequests';
import { usePrograms } from '@src/hooks/usePrograms';
import { PaymentRequestStatus, RuleType, } from '@src/types';
import { getDeadlineErrorMessage } from '@src/utilities/paymentRequestHelper';
import { useLocale } from 'pe-core-frontend';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SelectRequest.module.scss';
export const SelectRequest = ({ programId }) => {
    const { t, formatToLocalDate } = useLocale();
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const { find: findProgram, loading: loadingPrograms } = usePrograms();
    const { paymentRequests, loading: loadingPaymentRequests } = usePaymentRequests({
        programId,
        useCurrentUser: true,
        statuses: [PaymentRequestStatus.ACTIVE],
    });
    const program = findProgram(programId);
    const { getPolicy } = useMyBenefits({ program });
    const [paymentRequestId, setPaymentRequestId] = useState('');
    const eligibilityPolicy = getPolicy(RuleType.ELIGIBILITY, program);
    const approvalChainPolicy = getPolicy(RuleType.APPROVAL_CHAIN, program);
    let newTermDescription = '';
    let expenseDeadlineMessage = '';
    const paymentRequestPolicy = approvalChainPolicy?.paymentRequestApproval;
    if (paymentRequestPolicy) {
        if (paymentRequestPolicy.enableDeadline && paymentRequestPolicy.deadline) {
            const days = paymentRequestPolicy.deadline.minInterval?.days || 0;
            newTermDescription = t('New term description', {
                values: { days },
            });
        }
        if (paymentRequestPolicy.enableDocumentDeadline && paymentRequestPolicy.documentDeadline) {
            const [message, params] = getDeadlineErrorMessage(formatToLocalDate, false, paymentRequestPolicy.documentDeadline, new Date(), new Date());
            expenseDeadlineMessage = t(message, { values: params });
        }
    }
    useEffect(() => {
        if (eligibilityPolicy && !eligibilityPolicy.enableEligibilityLock) {
            // This program does not have eligibility lock enabled. Redirect to new payment request
            navigate(`/payments/new/${programId}`, { replace: true });
            return;
        }
        if (!loadingPaymentRequests && paymentRequests.length === 0) {
            // console.log('No payment requests found for this program. Redirect to new payment request');
            // No payment requests found for this program. Redirect to new payment request
            navigate(`/payments/new/${programId}`, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [program, loadingPrograms, loadingPaymentRequests, eligibilityPolicy]);
    const handleOk = () => {
        setError('');
        if (!paymentRequestId) {
            setError(t('Please select an option to continue'));
            return;
        }
        if (paymentRequestId === 'new') {
            // Create a new payment request
            navigate(`/payments/new/${programId}`);
            return;
        }
        navigate(`/payments/${paymentRequestId}/edit`);
    };
    if (loadingPrograms || loadingPaymentRequests) {
        return (_jsx(FullPageWrapper, { children: _jsx(TopBar, { backLink: '/' }) }));
    }
    return (_jsxs(FullPageWrapper, { children: [_jsx(TopBar, { backLink: '/' }), _jsx(PageContainer, { children: _jsxs(FlowContainer, { children: [_jsxs("div", { className: styles.form, children: [_jsxs("div", { className: styles.heading, children: [_jsx(Typography, { variant: 'h2', children: t('About your request') }), _jsx(Typography, { variant: 'label2', children: t('* required') })] }), _jsxs("div", { className: styles.content, children: [error && (_jsx(Alert, { type: 'danger', hideClose: true, display: true, className: 'mb-4', children: error })), program && _jsx(ProgramDetails, { program: program, showCompletionDate: true }), _jsxs(Typography, { className: 'my-4', variant: 'label1', children: [t('Which term is your expense related to?'), " *"] }), expenseDeadlineMessage && (_jsx(Typography, { variant: 'label2', weight: '400', className: 'mb-4', children: expenseDeadlineMessage })), _jsxs("div", { className: styles.options, children: [paymentRequests.map(paymentRequest => (_jsx("div", { className: styles.option, children: _jsx("div", { className: styles.optionContent, children: _jsx(RadioInput, { label: _jsx("div", { children: _jsxs(Typography, { variant: 'label1', children: [paymentRequest.termStartDate &&
                                                                            formatToLocalDate(paymentRequest.termStartDate, {
                                                                                dateStyle: 'medium',
                                                                            }), ' - ', paymentRequest.termEndDate &&
                                                                            formatToLocalDate(paymentRequest.termEndDate, {
                                                                                dateStyle: 'medium',
                                                                            })] }) }), name: 'paymentRequestId', value: `${paymentRequest.id}`, checked: paymentRequestId === `${paymentRequest.id}`, onChange: () => setPaymentRequestId(`${paymentRequest.id}`) }) }) }, paymentRequest.id))), _jsx("div", { className: styles.option, children: _jsx("div", { className: styles.optionContent, children: _jsx(RadioInput, { label: _jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(Typography, { variant: 'label1', children: t('New term') }), _jsx(Typography, { variant: 'label2', weight: '400', children: newTermDescription })] }), name: 'paymentRequestId', value: 'new', checked: paymentRequestId === 'new', onChange: () => setPaymentRequestId('new') }) }) })] })] })] }), _jsx(FormActions, { okText: t('Continue'), onOk: handleOk })] }) })] }));
};
